if (!customElements.get('puzzle-section')) {
  class PuzzleSection extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.handleMotionChange = () => this.setupMotion();
      this.motionQuery.addEventListener('change', this.handleMotionChange);
      this.setupHeroViewport();
      this.setupPatterns();
      this.setupMotion();
      document.addEventListener('shopify:block:select', (event) => {
        if (!this.contains(event.target)) return;
        this.querySelectorAll('.pz-reveal').forEach((element) => element.classList.add('is-visible'));
      }, { signal: this.controller.signal });
    }

    disconnectedCallback() {
      this.stopMotion();
      this.viewportObserver?.disconnect();
      this.controller?.abort();
      this.controller = null;
      this.motionQuery?.removeEventListener('change', this.handleMotionChange);
    }

    setupHeroViewport() {
      if (!this.querySelector('.pz-hero')) return;
      const main = this.closest('main') || this;
      const update = () => {
        const offset = `${Math.max(0, Math.round(main.getBoundingClientRect().top + window.scrollY))}px`;
        if (this.style.getPropertyValue('--pz-viewport-offset') !== offset) {
          this.style.setProperty('--pz-viewport-offset', offset);
        }
      };
      window.addEventListener('resize', update, { passive: true, signal: this.controller.signal });
      if ('ResizeObserver' in window) {
        this.viewportObserver = new ResizeObserver(update);
      }
      const observeHeaders = () => {
        this.viewportObserver?.disconnect();
        document.querySelectorAll('.shopify-section-group-header-group, .preview-header').forEach((header) => this.viewportObserver?.observe(header));
        update();
      };
      ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((name) => {
        document.addEventListener(name, observeHeaders, { signal: this.controller.signal });
      });
      observeHeaders();
    }

    stopMotion() {
      this.revealObserver?.disconnect();
      this.motionController?.abort();
      if (this.frame != null) window.cancelAnimationFrame(this.frame);
      this.frame = null;
      this.classList.remove('is-motion-ready');
      this.querySelector('.pz-rise__image')?.style.removeProperty('--pz-rise-y');
    }

    setupMotion() {
      this.stopMotion();
      if (this.dataset.motion !== 'true' || this.motionQuery.matches || window.Shopify?.designMode) return;
      if (!('IntersectionObserver' in window)) return;

      this.revealObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { threshold: .12, rootMargin: '0px 0px -8% 0px' });
      this.querySelectorAll('.pz-reveal').forEach((element) => this.revealObserver.observe(element));
      this.classList.add('is-motion-ready');

      const stage = this.querySelector('[data-parallax-stage]');
      const image = this.querySelector('.pz-rise__image');
      if (!stage || !image) return;
      this.motionController = new AbortController();
      const update = () => {
        this.frame = null;
        const viewportHeight = window.innerHeight;
        const progress = Math.min(1, Math.max(0, (viewportHeight - stage.getBoundingClientRect().top) / viewportHeight));
        const eased = 1 - Math.pow(1 - progress, 2);
        image.style.setProperty('--pz-rise-y', `${(eased * viewportHeight * .06).toFixed(1)}px`);
      };
      const schedule = () => {
        if (this.frame == null) this.frame = window.requestAnimationFrame(update);
      };
      const options = { passive: true, signal: this.motionController.signal };
      window.addEventListener('scroll', schedule, options);
      window.addEventListener('resize', schedule, options);
      update();
    }

    setupPatterns() {
      const paths = [...this.querySelectorAll('.pz-pattern [data-piece]')];
      const controls = this.querySelector('[data-pattern-controls]');
      if (!paths.length || !controls) return;
      const pieces = [...new Set(paths.map((path) => path.dataset.piece))].sort((a, b) => Number(a) - Number(b));
      let index = Math.max(0, pieces.indexOf('56'));
      const highlight = (piece) => {
        index = pieces.indexOf(piece);
        paths.forEach((path) => path.setAttribute('fill', path.dataset.piece === piece ? '#a4513a' : path.dataset.tone));
      };
      const options = { signal: this.controller.signal };
      this.querySelectorAll('.pz-pattern').forEach((svg) => {
        const select = (event) => {
          if (event.target.dataset.piece) highlight(event.target.dataset.piece);
        };
        svg.addEventListener('pointerover', select, options);
        svg.addEventListener('click', select, options);
      });
      controls.querySelector('[data-pattern-prev]').addEventListener('click', () => highlight(pieces[(index - 1 + pieces.length) % pieces.length]), options);
      controls.querySelector('[data-pattern-next]').addEventListener('click', () => highlight(pieces[(index + 1) % pieces.length]), options);
      controls.hidden = false;
      highlight(pieces[index]);
    }
  }
  customElements.define('puzzle-section', PuzzleSection);
}
